import React, { useEffect, useState } from 'react'
import { Card, Container, Drawer, Grid } from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import {
  getProjectDetails,
  getProjectRequestsDetails,
} from '../../../actions/projectActions'
import Loader from '../../Loader/Loader'
import Message from '../../Message/Message'
import {
  getActivityByProject,
  markActivityAsRead,
} from '../../../actions/activityActions'
import ActivityPanel from '../../Activity/ActivityPanel'
import ProjectActionsAdmin from './ProjectActionsAdmin'
import ProjectActionsUser from './ProjectActionsUser'
import ProjectInfo from './ProjectInfo'
import ToastMessage from '../../ToastMessage/ToastMessage'
import {
  PROJECT_ADD_PO_DOCS_RESET,
  PROJECT_EXIT_RESET,
  PROJECT_SUBMIT_QUOTE_RESET,
  PROJECT_UPDATE_RESET,
  PROJECT_UPDATE_QUOTE_RESET,
  PROJECT_MARK_AS_PAID_RESET,
  PROJECT_DELIVERABLES_RESET,
  PROJECT_DETAILS_RESET,
} from '../../../constants/projectConstants'
import ProjectAttachments from './ProjectAttachments'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'
import ProjectHeader from './ProjectHeader'
import './projectDetails.scss'

const ProjectDetail = () => {
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()

  const [showActivityPanel, setShowActivityPanel] = useState(false)
  const [submitQuoteToast, setSubmitQuoteToast] = useState(false)
  const [updateQuoteToast, setUpdateQuoteToast] = useState(false)
  const [markAsPaidToast, setMarkAsPaidToast] = useState(false)
  const [deliverablesToast, setDeliverablesToast] = useState(false)
  const [downloadToast, setDownloadToast] = useState(false)

  const projectId = location.pathname.substring(
    location.pathname.lastIndexOf('/') + 1
  )

  const { loading, error, project } = useSelector(
    (state) => state.projectDetails
  )
  const { userInfo } = useSelector((state) => state.userLogin)
  const { success: successSubmitQuote } = useSelector(
    (state) => state.projectSubmitQuote
  )
  const { success: successUpdateQuote } = useSelector(
    (state) => state.projectUpdateQuote
  )
  const { success: successUpdate } = useSelector((state) => state.projectUpdate)
  const { exit } = useSelector((state) => state.projectExit)
  const { success: addPODocsSuccess } = useSelector(
    (state) => state.projectAddPODocs
  )
  const { success: markAsPaidSuccess } = useSelector(
    (state) => state.projectMarkAsPaid
  )
  const { success: deliverablesSuccess } = useSelector(
    (state) => state.projectDeliverables
  )
  const { loading: fileLoading } = useSelector((state) => state.fileDownload)

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectDetails(projectId))
      dispatch(getActivityByProject(projectId))
      dispatch(markActivityAsRead(projectId))
    }

    return () => {
      // Reset project data when component unmounts
      dispatch({ type: PROJECT_DETAILS_RESET })
      dispatch({ type: PROJECT_EXIT_RESET })
    }
  }, [dispatch, history, projectId])

  useEffect(() => {
    if (successSubmitQuote) {
      setSubmitQuoteToast(true)
      dispatch({ type: PROJECT_SUBMIT_QUOTE_RESET })
      dispatch(getProjectDetails(projectId))
      dispatch(getActivityByProject(projectId))
    }

    if ((successUpdateQuote || successUpdate) && !exit) {
      setUpdateQuoteToast(true)
      dispatch({ type: PROJECT_UPDATE_QUOTE_RESET })
      dispatch({ type: PROJECT_UPDATE_RESET })

      dispatch(getProjectDetails(project._id))
      dispatch(getActivityByProject(project._id))
    }

    if (successUpdate && exit) {
      dispatch({ type: PROJECT_UPDATE_RESET })
      dispatch({ type: PROJECT_UPDATE_QUOTE_RESET })
      dispatch({ type: PROJECT_EXIT_RESET })

      history.push('/myquality/dashboard')
    }

    if (addPODocsSuccess) {
      dispatch(getProjectRequestsDetails(project._id))
      dispatch(getActivityByProject(project._id))
      dispatch({ type: PROJECT_ADD_PO_DOCS_RESET })
    }

    if (markAsPaidSuccess) {
      setMarkAsPaidToast(true)
      dispatch(getProjectDetails(project._id))
      dispatch(getActivityByProject(project._id))
      dispatch({ type: PROJECT_MARK_AS_PAID_RESET })
    }

    if (deliverablesSuccess) {
      setDeliverablesToast(true)
      dispatch(getProjectDetails(project._id))
      dispatch(getActivityByProject(project._id))
      dispatch({ type: PROJECT_DELIVERABLES_RESET })
    }

    if (error) {
      history.push('/myquality/projectnotfound')
    }

    if (fileLoading) {
      setDownloadToast(true)
    }

    dispatch({ type: SIDE_NAV_CLOSE })
  }, [
    dispatch,
    successSubmitQuote,
    successUpdateQuote,
    projectId,
    exit,
    history,
    successUpdate,
    addPODocsSuccess,
    project,
    markAsPaidSuccess,
    deliverablesSuccess,
    error,
    fileLoading,
  ])

  return (
    <Container className="project-details">
      {error && <Message variant="error">{error}</Message>}
      {loading ? (
        <Loader />
      ) : (
        !error && (
          <>
            <ProjectHeader setShowActivityPanel={setShowActivityPanel} />
            <Grid container spacing={4}>
              <Grid item md={8} sm={12} xs={12}>
                <Card className="p-24 m-p-16">
                  <ProjectInfo />
                  <ProjectAttachments />
                </Card>
              </Grid>
              <Grid item md={4} sm={12} xs={12}>
                {userInfo.isAdmin ? (
                  <ProjectActionsAdmin />
                ) : (
                  <ProjectActionsUser
                    setShowActivityPanel={setShowActivityPanel}
                  />
                )}
              </Grid>
            </Grid>

            <Drawer
              anchor="right"
              open={showActivityPanel}
              onClose={() => setShowActivityPanel(false)}
              className="side-panel"
            >
              <ActivityPanel />
            </Drawer>
          </>
        )
      )}
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Quote submitted</p>
            <p className="fs14 m-b-0">
              Your quote has been submitted to the customer.
            </p>
          </>
        }
        open={submitQuoteToast}
        setOpen={setSubmitQuoteToast}
      />
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Quote updated</p>
            <p className="fs14 m-b-0">Your quote has been updated.</p>
          </>
        }
        open={updateQuoteToast}
        setOpen={setUpdateQuoteToast}
      />
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Approved</p>
            <p className="fs14 m-b-0">
              The purchase order was successfully approved.
            </p>
          </>
        }
        open={markAsPaidToast}
        setOpen={setMarkAsPaidToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Deliverables submitted</p>
            <p className="fs14 m-b-0">
              You have successfully submitted deliverables to the customer.
            </p>
          </>
        }
        open={deliverablesToast}
        setOpen={setDeliverablesToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Downloading</p>
            <p className="fs14 m-b-0">
              We are preparing your files for download. Please wait...
            </p>
          </>
        }
        open={downloadToast}
        setOpen={setDownloadToast}
      />
    </Container>
  )
}

export default ProjectDetail
